import moment from "moment";
import { db_timestamp, getDateFormat } from "@/utils/date-format";

const DB_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

export function resolveDateBoxValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d+$/.test(trimmed)) {
    // Only treat digit-only strings as Unix timestamps at the two canonical
    // epoch lengths: 10 digits (seconds) or 13 digits (milliseconds). Other
    // numeric strings — e.g. YYYYMMDD (8 digits) or Excel serial dates
    // (~5 digits) — must NOT be silently coerced to the wrong date. They
    // fall through and surface as a validation warning.
    if (trimmed.length !== 10 && trimmed.length !== 13) return undefined;

    const timestampMs =
      trimmed.length === 10 ? Number(trimmed) * 1000 : Number(trimmed);
    const parsed = moment(timestampMs);
    if (!parsed.isValid()) return undefined;

    return parsed.format(DB_DATE_TIME_FORMAT);
  }

  // Parse strictly against the tenant's configured date format so ambiguous
  // values like "01/02/2024" follow the dateFormat.date setting instead of
  // V8's US-centric Date parsing. Strict-mode tokens are exact about zero
  // padding (DD rejects "1", D rejects "01"), so try both variants.
  const paddingVariants = (format: string) => [
    format,
    format.replace("DD", "D").replace("MM", "M").replace("HH", "H"),
  ];

  const dateOnlyFormats = paddingVariants(
    getDateFormat({ includeTime: false })
  );
  const dateTimeFormats = [
    ...paddingVariants(getDateFormat({ includeTime: true })),
    ...dateOnlyFormats.flatMap((format) => [
      `${format} HH:mm:ss`,
      `${format} H:mm:ss`,
      `${format} HH:mm`,
      `${format} H:mm`,
    ]),
  ];

  const withTime = moment(trimmed, dateTimeFormats, true);
  if (withTime.isValid()) return withTime.format(DB_DATE_TIME_FORMAT);

  const dateOnly = moment(trimmed, dateOnlyFormats, true);
  if (dateOnly.isValid()) return dateOnly.format("YYYY-MM-DD");

  return undefined;
}

// Prepare a resolved dateBox value for storage as a custom attribute.
// Time-bearing values from resolveDateBoxValue are local time, but display
// (monolithMoment) assumes stored timestamps are UTC — convert them.
// Date-only values are stored as-is, matching form-entered dateBox values.
export function toDbDateBoxValue(value: unknown): unknown {
  if (typeof value === "string" && value.length > 10) {
    return db_timestamp(value);
  }
  return value;
}
